import { processImage, processMultipleImages } from "@services/openaiApi";
import { toast } from 'react-toastify';
import { transcribeAudio } from '../../services/openaiApi';
import { processDocxOnServer, isMobileDevice, hasMammothLimitations } from '../../services/docxService';
import { parseObsidianNote } from '../../services/obsidianParser';
import i18n from 'i18next';
import { mimePulito, estensioneDi } from '@config/uploadFormats';
import { estraiViaStorage, passaDalCondottoLargo } from '@services/condottoLargo';
import { puoStaccareLAudio, staccaTracciaAudio } from '@services/tracciaAudio';
import { pubblicaMetrichePdf } from '@utils/pdfAnalytics';

// ─────────────────────────────────────────────────────────────────────────
// IL FILE RESTA NEL BROWSER, SALE SOLO IL TESTO.
//
// Prima un PDF da 10 MB faceva: FileReader -> base64 byte per byte (secondi
// di thread principale bloccato) -> atob -> Blob -> multipart al server, che
// lo rileggeva da capo. Il PDF saliva INTERO, per ricavarne un testo che
// pdf.js nel browser avrebbe letto in un secondo e gratis. La lettura locale
// esisteva, ma solo sopra i 20 MiB.
//
// Ora l'ordine e' uno per tutti i formati e a qualsiasi taglia:
//
//   1. si prova in locale (pdf.js nel worker, mammoth, JSZip, ffmpeg per
//      staccare l'audio dai video);
//   2. se la resa e' scarsa (scansione) le pagine escono come JPEG dal worker
//      e vanno all'OCR a lotti: sale un quinto del PDF, mai il PDF;
//   3. solo se il locale non puo' (browser vecchio, file cifrato, formato
//      Office del 2003) il file sale: multipart sotto i 20 MiB, Storage sopra.
//
// Le librerie pesanti si caricano con `await import()` dentro le funzioni
// che le usano, per non finire nel chunk iniziale di ogni route.
// ─────────────────────────────────────────────────────────────────────────

// pdf.js viene dall'origine del sito (public/pdfjs, copiato da
// scripts/copia-pdfjs.js), non da un CDN: vedi il worker per il perche'.
const BASE_PDFJS = '/pdfjs';

const loadPdfjs = async () => {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = `${BASE_PDFJS}/pdf.worker.min.js`;
  return pdfjs;
};

const opzioniPdfjs = (data) => ({
  data,
  cMapUrl: `${BASE_PDFJS}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `${BASE_PDFJS}/standard_fonts/`,
});

// Configurazione URL API per ambiente
let API_URL;

if (process.env.REACT_APP_ENVIRONMENT === "development") {
  API_URL = "http://" + process.env.REACT_APP_API_URL_DEVELOPMENT;
} else if (process.env.REACT_APP_ENVIRONMENT === "preproduction") {
  API_URL = "https://" + process.env.REACT_APP_API_URL_PREPRODUCTION;
} else {
  API_URL = "https://" + process.env.REACT_APP_API_URL_PRODUCTION;
}

// Pulisci URL e rimuovi eventuali virgolette
const API_BASE_URL = API_URL ? API_URL.replace(/["']/g, "") : "http://localhost:8080";

const MIME_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const MIME_PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const MIME_DOC = 'application/msword';
const MIME_PPT = 'application/vnd.ms-powerpoint';

// Sotto questa lunghezza il documento non basta a generare materiali. Il
// numero e' lo stesso di sempre; la stringa dell'errore la legge actions.js
// per classificare il fallimento, quindi non va cambiata da sola.
const MIN_CARATTERI_DOCUMENTO = 300;

/**
 * ArrayBuffer -> base64 a blocchi. La versione di prima concatenava una
 * stringa un byte alla volta: su 20 MB erano secondi di thread principale
 * fermo. Qui si va a blocchi da 32 KB con `String.fromCharCode.apply`. Per i
 * File c'e' `leggiComeBase64`, che lo fa fare al browser.
 */
export const arrayBufferToBase64 = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  const BLOCCO = 32768;
  let binario = '';
  for (let i = 0; i < bytes.length; i += BLOCCO) {
    binario += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + BLOCCO, bytes.length)));
  }
  return btoa(binario);
};

/**
 * Il file come ArrayBuffer, via FileReader: `Blob.arrayBuffer()` manca su
 * Safari sotto la 14 e nel jsdom dei test.
 */
const leggiComeArrayBuffer = (file) =>
  new Promise((risolvi, rifiuta) => {
    const lettore = new FileReader();
    lettore.onload = () => risolvi(lettore.result);
    lettore.onerror = () => rifiuta(new Error('Errore durante la lettura del file'));
    lettore.readAsArrayBuffer(file);
  });

/** Il file in base64, letto dal browser con readAsDataURL: non blocca. */
const leggiComeBase64 = (file) =>
  new Promise((risolvi, rifiuta) => {
    const lettore = new FileReader();
    lettore.onload = () => risolvi(String(lettore.result || '').split(',')[1] || '');
    lettore.onerror = () => rifiuta(new Error('Errore durante la lettura del file'));
    lettore.readAsDataURL(file);
  });

// Extract text from image - Delegata completamente al backend
export const extractTextFromImage = async image => {
  try {
    // Log per debug
    console.log(`🖼️ Elaborazione immagine: ${image.name || 'unnamed'} (${(image.size / 1024).toFixed(1)}KB)`);

    // Delega completamente al backend tramite processImage
    // Il backend gestirà l'OCR con Gemini Vision API
    const extractedText = await processImage(image);

    if (!extractedText) {
      throw new Error('Nessun testo estratto dall\'immagine, riprova');
    }

    // Valida il testo estratto
    if (typeof extractedText !== 'string' || extractedText.trim().length === 0) {
      throw new Error('Il testo estratto è vuoto o non valido');
    }

    console.log(`✅ Testo estratto con successo: ${extractedText.length} caratteri`);
    return extractedText;

  } catch (error) {
    console.error('Error processing image:', error);

    // Gestione errori specifica per tipo
    let errorMessage = 'Errore durante l\'elaborazione dell\'immagine';

    if (error.message) {
      if (error.message.includes('troppo grande')) {
        errorMessage = 'L\'immagine è troppo grande. Prova a ridurre la dimensione o la risoluzione.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'L\'elaborazione ha richiesto troppo tempo. Riprova con un\'immagine più piccola.';
      } else if (error.message.includes('rate limit') || error.message.includes('troppe richieste')) {
        errorMessage = 'Troppe richieste. Attendi qualche secondo e riprova.';
      } else {
        errorMessage = error.message;
      }
    }

    return { message: errorMessage };
  }
};

// ─────────────────────────────────────────────────────────────────────────
// QUANDO LA LETTURA LOCALE HA RESO
// ─────────────────────────────────────────────────────────────────────────

/**
 * La stessa soglia del server (`pdf_extractor.py`, MIN_CHARS_PER_PAGE): sotto
 * i cento caratteri a pagina il testo non e' il documento, e' quello che
 * pdf.js ha trovato intorno a una scansione.
 */
export const MIN_CARATTERI_PER_PAGINA = 100;

/**
 * Sopra questa quota di pagine vuote il PDF e' una scansione anche se la
 * media regge. Il caso vero: una scansione con la copertina e l'indice in
 * testo, che con il vecchio criterio (300 caratteri in tutto) passava come
 * riuscita e lo studente si ritrovava con l'indice al posto del libro.
 */
export const QUOTA_MASSIMA_PAGINE_VUOTE = 0.5;

/**
 * Vero se la lettura locale NON basta e serve l'OCR.
 * @param {{caratteri:number, pagine:number, pagineSenzaTesto?:number}} conti
 */
export const testoScarso = ({ caratteri = 0, pagine = 0, pagineSenzaTesto = 0 } = {}) => {
  if (!pagine) return true;
  if (caratteri / pagine < MIN_CARATTERI_PER_PAGINA) return true;
  if (pagineSenzaTesto / pagine > QUOTA_MASSIMA_PAGINE_VUOTE) return true;
  return false;
};

// Oltre queste pagine l'OCR nel browser non si tenta: a ~150 KB a pagina
// sono 150 MB di JPEG, e prima ancora sono ore di modello.
export const MAX_PAGES_FOR_OCR_FALLBACK = 1000;

// Intervallo minimo tra aggiornamenti del progresso (in ms) - evita che il testo cambi troppo velocemente
const PROGRESS_UPDATE_INTERVAL_MS = 1500;

/**
 * Crea un throttle per gli aggiornamenti del progresso
 * @param {Function} callback - Funzione da throttlare
 * @param {number} minInterval - Intervallo minimo tra chiamate (ms)
 * @returns {Function} - Funzione throttled
 */
const createProgressThrottle = (callback, minInterval = PROGRESS_UPDATE_INTERVAL_MS) => {
  let lastUpdateTime = 0;
  let lastMessage = '';

  return (progressData) => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTime;

    // Aggiorna sempre se è completato o se il messaggio è significativamente diverso
    const isCompletionPhase = progressData.phase === 'completed' || progressData.progress >= 100;
    const isNewPhase = progressData.phase !== lastMessage.split(':')[0];

    if (isCompletionPhase || isNewPhase || timeSinceLastUpdate >= minInterval) {
      lastUpdateTime = now;
      lastMessage = `${progressData.phase}:${progressData.message}`;
      callback(progressData);
    }
  };
};

// Large file compression configuration
const MAX_PAGES_FOR_COMPRESSION = 1000; // Max pages to process

/**
 * Comprime un PDF grande convertendo le pagine in immagini JPEG compresse
 * e ricreando un nuovo PDF più leggero.
 *
 * Il PDF compresso viene poi inviato al server normalmente.
 *
 * @param {File} file - Il file PDF originale
 * @param {Function} onProgress - Callback per progress
 * @returns {Promise<File>} - File PDF compresso
 */
export const compressLargePdf = async (file, onProgress = null) => {
  const originalSizeMB = file.size / (1024 * 1024);
  console.log(`🗜️ Compressione PDF: ${file.name} (${originalSizeMB.toFixed(1)} MB)`);

  if (onProgress) {
    onProgress({
      phase: 'compressing',
      progress: 5,
      message: 'Analisi PDF per compressione...'
    });
  }

  const { getDocument } = await loadPdfjs();
  const { PDFDocument } = await import('pdf-lib');

  // Carica il PDF con pdf.js per renderizzare le pagine
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = getDocument(opzioniPdfjs(arrayBuffer));
  const sourcePdf = await loadingTask.promise;
  const pageCount = sourcePdf.numPages;

  console.log(`📄 PDF con ${pageCount} pagine - inizio compressione`);

  if (pageCount > MAX_PAGES_FOR_COMPRESSION) {
    throw new Error(`PDF troppo lungo (${pageCount} pagine). Massimo supportato: ${MAX_PAGES_FOR_COMPRESSION}`);
  }

  // Calcola qualità JPEG basata sulla dimensione originale
  let jpegQuality = 0.7;
  if (originalSizeMB > 200) {
    jpegQuality = 0.5;
  } else if (originalSizeMB > 150) {
    jpegQuality = 0.55;
  } else if (originalSizeMB > 100) {
    jpegQuality = 0.6;
  }

  // Scala DPI
  let dpiScale = 1.5;
  if (originalSizeMB > 200) {
    dpiScale = 1.0;
  } else if (originalSizeMB > 150) {
    dpiScale = 1.2;
  }

  console.log(`⚙️ Parametri compressione: qualità JPEG=${jpegQuality}, scala DPI=${dpiScale}`);

  // Crea nuovo PDF con pdf-lib
  const newPdf = await PDFDocument.create();

  for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
    if (onProgress) {
      const progressPercent = 5 + Math.round((pageNum / pageCount) * 80);
      onProgress({
        phase: 'compressing',
        progress: progressPercent,
        message: `Compressione pagina ${pageNum}/${pageCount}...`
      });
    }

    // Renderizza pagina su canvas
    const page = await sourcePdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: dpiScale });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: ctx,
      viewport: viewport
    }).promise;

    // Converti in JPEG compresso
    const jpegDataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
    const jpegBase64 = jpegDataUrl.split(',')[1];
    const jpegBytes = Uint8Array.from(atob(jpegBase64), c => c.charCodeAt(0));

    // Aggiungi immagine al nuovo PDF
    const jpegImage = await newPdf.embedJpg(jpegBytes);
    const newPage = newPdf.addPage([viewport.width, viewport.height]);
    newPage.drawImage(jpegImage, {
      x: 0,
      y: 0,
      width: viewport.width,
      height: viewport.height,
    });

    // Cleanup
    await page.cleanup();
  }

  await sourcePdf.destroy();

  // Salva il nuovo PDF
  if (onProgress) {
    onProgress({
      phase: 'finalizing',
      progress: 90,
      message: 'Creazione PDF compresso...'
    });
  }

  const compressedPdfBytes = await newPdf.save();
  const compressedBlob = new Blob([compressedPdfBytes], { type: 'application/pdf' });
  const compressedSizeMB = compressedBlob.size / (1024 * 1024);

  // Crea File object per compatibilità
  const compressedFile = new File(
    [compressedBlob],
    file.name.replace('.pdf', '_compressed.pdf'),
    { type: 'application/pdf' }
  );

  const reductionPercent = ((1 - compressedSizeMB / originalSizeMB) * 100).toFixed(0);
  console.log(`✅ Compressione completata: ${originalSizeMB.toFixed(1)} MB → ${compressedSizeMB.toFixed(1)} MB (${reductionPercent}% riduzione)`);

  if (onProgress) {
    onProgress({
      phase: 'compressed',
      progress: 95,
      message: `Compresso: ${compressedSizeMB.toFixed(1)} MB (da ${originalSizeMB.toFixed(1)} MB)`
    });
  }

  return compressedFile;
};

/**
 * Il PDF al server, in multipart, per l'estrazione con Gemini.
 *
 * E' il ripiego di quando il browser non ce la fa (pdf.js non lo apre, o e'
 * una scansione e OffscreenCanvas manca), e vale sotto i 20 MiB: sopra c'e'
 * il condotto largo. Il File va nel FormData COSI' COM'E': prima passava da
 * base64 -> atob -> Blob, tre copie in memoria per non cambiare un byte.
 * La stringa base64 e' ancora accettata per chi arriva da getExtractedText.
 *
 * @param {File|Blob|string} sorgente
 */
export const extractTextFromPdf = async (sorgente, onProgress = null, fileName = 'PDF') => {
  const startTime = Date.now();

  console.log('📄 Invio PDF al backend per estrazione...');

  if (onProgress) {
    onProgress({
      phase: 'upload',
      progress: 10,
      message: 'Invio PDF al server...'
    });
  }

  // Crea FormData. Il File entra con il suo nome; solo un Blob senza nome ne
  // riceve uno.
  const formData = new FormData();
  if (typeof sorgente === 'string') {
    formData.append('file', base64ToBlob(sorgente, 'application/pdf'), fileName || 'document.pdf');
  } else {
    formData.append('file', sorgente);
  }

  if (onProgress) {
    onProgress({
      phase: 'processing',
      progress: 30,
      message: 'Elaborazione in corso...'
    });
  }

  // Chiama backend
  const response = await fetch(`${API_BASE_URL}/api/extract-pdf`, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errore = new Error(errorData.detail || `Errore server: ${response.status}`);
    // Il codice HTTP viaggia a parte: un 413 e un 500 chiedono allo studente
    // cose diverse (STU-424).
    errore.status = response.status;
    throw errore;
  }

  const result = await response.json();
  const duration = Date.now() - startTime;

  const { text, metadata = {} } = result;
  console.log(`✅ Estrazione completata: ${(text || '').length} caratteri in ${duration}ms`);
  console.log(`📊 Metodo: ${metadata.method}, Pagine: ${metadata.pages}`);

  // Un pezzo perso puo' essere accettabile; un pezzo perso non dichiarato
  // mai. Il server adesso manda i conti anche su questa strada (prima li
  // mandava solo il condotto largo): stessa chiave i18n, stesso pattern
  // console+toast del troncamento 500k (useGetDocuments).
  if (metadata.parziale) {
    const persi = metadata.chunks_failed || 0;
    const totali = metadata.chunks_total || 0;
    console.warn(
      `[documenti] ESTRAZIONE PARZIALE di "${fileName}": ${persi} blocchi su ${totali} persi` +
      (metadata.failed_pages?.length ? ` (pagine ${metadata.failed_pages.join(', ')})` : '')
    );
    toast.warn(
      i18n.t('upload:messages.extractionPartial', { fileName, lost: persi, total: totali }),
      { autoClose: 8000, toastId: `extraction-partial-${fileName}` }
    );
  }

  // Verifica contenuto
  if (!text || text.trim().length === 0) {
    throw new Error('Il PDF non contiene testo leggibile');
  }

  if (onProgress) {
    onProgress({
      phase: 'completed',
      progress: 100,
      message: `Completato: ${text.length} caratteri`
    });
  }

  return text;
};

// Extract text from Word
export const extractTextFromWord = async (base64Data) => {
  // Carica mammoth dinamicamente (fuori dal bundle iniziale)
  const { default: mammoth } = await import('mammoth/mammoth.browser');

  // Fix per mobile/iPad: usa la conversione nativa del browser per evitare corruzioni
  const cleanBase64 = base64Data.replace(/[^A-Za-z0-9+/=]/g, '');

  console.log('🔧 Starting DOCX extraction, mobile device:', isMobileDevice());

  try {
    // Mobile/iPad fix: usa fetch con Data URL per conversione più affidabile
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    let arrayBuffer;
    let conversionMethod = 'unknown';

    if (isIOS || isMobile) {
      // Metodo più sicuro per dispositivi mobile con retry logic
      let fetchSuccess = false;

      // Prova prima il metodo fetch (più affidabile)
      try {
        console.log('📱 Trying fetch method for mobile DOCX conversion...');
        const response = await fetch(`data:${MIME_DOCX};base64,${cleanBase64}`);

        if (response.ok) {
          arrayBuffer = await response.arrayBuffer();
          fetchSuccess = true;
          conversionMethod = 'fetch';
          console.log('✅ Fetch method successful');
        } else {
          throw new Error(`Fetch failed with status: ${response.status}`);
        }
      } catch (fetchError) {
        console.warn('⚠️ Fetch method failed, trying manual conversion:', fetchError.message);
        fetchSuccess = false;
      }

      // Fallback al metodo manuale se fetch fallisce
      if (!fetchSuccess) {
        try {
          console.log('🔄 Using manual base64 conversion for mobile...');
          const binaryString = atob(cleanBase64);
          arrayBuffer = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            arrayBuffer[i] = binaryString.charCodeAt(i);
          }
          conversionMethod = 'manual';
          console.log('✅ Manual conversion successful');
        } catch (manualError) {
          console.error('❌ Manual conversion also failed:', manualError);
          throw new Error(`Both fetch and manual conversion failed: ${manualError.message}`);
        }
      }
    } else {
      // Metodo originale per desktop
      console.log('🖥️ Using desktop method for DOCX conversion...');
      const binaryString = atob(cleanBase64);
      arrayBuffer = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        arrayBuffer[i] = binaryString.charCodeAt(i);
      }
      conversionMethod = 'desktop';
    }

    // Validazione ArrayBuffer prima del parsing
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      throw new Error('File .docx corrotto o vuoto dopo la conversione');
    }

    // Verifica che il file sia un ZIP valido (i file .docx sono archivi ZIP)
    const signature = new Uint8Array(arrayBuffer.slice(0, 4));
    const zipSignature = [0x50, 0x4B, 0x03, 0x04]; // "PK" signature for ZIP files
    const isValidZip = signature.every((byte, index) => byte === zipSignature[index]);

    if (!isValidZip) {
      throw new Error('File .docx non valido: firma ZIP mancante');
    }

    console.log(`📖 Attempting mammoth extraction with ${conversionMethod} method...`);
    const { value } = await mammoth.extractRawText({ arrayBuffer });

    if (!value || value.trim().length === 0) {
      throw new Error('Nessun testo estratto dal documento word, riprova');
    }

    console.log(`✅ DOCX extraction successful using ${conversionMethod} method`);
    return value;
  } catch (error) {
    console.error('❌ Error processing word with mammoth:', error);

    // Determina se provare il server fallback in base al tipo di errore
    const isCriticalError = error.message.includes('corrupt') ||
                           error.message.includes('zip') ||
                           error.message.includes('End of data reached') ||
                           error.message.includes('Both fetch and manual conversion failed');

    const isLimitedDevice = hasMammothLimitations();

    // Usa il server fallback solo per errori critici o dispositivi con limitazioni note
    const shouldTryServerFallback = isCriticalError || isLimitedDevice;

    console.log(`🤔 Should try server fallback? ${shouldTryServerFallback} (Critical: ${isCriticalError}, Limited: ${isLimitedDevice})`);

    if (shouldTryServerFallback) {
      try {
        console.log('🔄 Trying server-side fallback for .docx processing...');
        const serverExtractedText = await processDocxOnServer(cleanBase64, 'document.docx');

        if (serverExtractedText && serverExtractedText.trim().length > 0) {
          console.log('✅ Server-side fallback successful');
          return serverExtractedText;
        }
      } catch (serverError) {
        console.error('❌ Server-side fallback also failed:', serverError);
      }
    }

    // Se tutto fallisce, restituisci messaggio di errore user-friendly
    if (isMobileDevice() && isCriticalError) {
      return {
        message: 'Errore nel caricamento del file .docx su dispositivo mobile. Soluzioni:\n' +
                '• Prova a convertire il file in formato PDF\n' +
                '• Riapri e salva il file in Word\n' +
                '• Prova da un computer desktop'
      };
    }

    return { message: error.message };
  }
};

/**
 * Il testo delle diapositive di un PPTX, dall'archivio ZIP che il PPTX e'.
 *
 * Le diapositive escono nell'ordine del numero nel nome (`slide2.xml` prima
 * di `slide10.xml`): l'ordine di `zip.folder().forEach` e' quello dell'indice
 * dell'archivio, che per PowerPoint e' alfabetico, e la dieci finiva prima
 * della due.
 *
 * @param {Blob|string} sorgente il File oppure la stringa binaria per JSZip
 */
const testoDalleDiapositive = async (sorgente) => {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(sorgente);

  const diapositive = [];
  const cartella = zip.folder('ppt/slides');
  if (cartella) {
    cartella.forEach((percorso, voce) => {
      const numero = /slide(\d+)\.xml$/i.exec(percorso);
      if (numero && !voce.dir) diapositive.push({ ordine: Number(numero[1]), voce });
    });
  }
  diapositive.sort((a, b) => a.ordine - b.ordine);

  const contenuti = await Promise.all(diapositive.map(({ voce }) => voce.async('string')));
  let text = '';
  contenuti.forEach((xml) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, 'application/xml');
    const textElements = xmlDoc.getElementsByTagName('a:t');
    for (let i = 0; i < textElements.length; i++) {
      text += textElements[i].textContent + ' ';
    }
  });
  return text;
};

// Extract text from PPTX
export const extractTextFromPpt = async (base64Data) => {
  try {
    const cleanBase64 = base64Data.replace(/[^A-Za-z0-9+/=]/g, '');
    const binaryString = atob(cleanBase64);
    const text = await testoDalleDiapositive(binaryString);

    if (!text) throw new Error('Nessun testo estratto dal documento ppt, riprova');

    return text;
  } catch (error) {
    console.error('Error processing ppt:', error);
    return { message: error.message };
  }
};

// Convert a base64 string to a Blob
export const base64ToBlob = (base64, mimeType = 'audio/mp4') => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
    const slice = byteCharacters.slice(offset, offset + 1024);
    const byteNumbers = Array.from(slice, char => char.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

/**
 * Il messaggio che vede chi sceglie un formato che non sappiamo leggere.
 *
 * Prima usciva "Nessun testo estratto", che dice all'utente una cosa falsa:
 * che il suo file e' rotto. Non lo e': non lo leggiamo. Qui glielo diciamo, e
 * gli diciamo cosa fare.
 *
 * Come in `plannerToScheduleConverter`: questo modulo non e' un componente,
 * non ha `useTranslation`, usa l'istanza i18next gia' avviata dall'app. Se non
 * c'e' (test, import isolato) torna il testo di riserva invece di lanciare.
 */
const messaggioFormatoNonSupportato = (typeFile, fileName) => {
  const formato = estensioneDi(fileName) || mimePulito(typeFile) || '?';
  const riserva = `Formato non supportato (${formato}). Convertilo in PDF o DOCX e ricaricalo.`;
  try {
    if (!i18n || !i18n.isInitialized || typeof i18n.t !== 'function') return riserva;
    const testo = i18n.t('file.unsupportedFormat', { ns: 'errors', defaultValue: riserva, format: formato });
    return typeof testo === 'string' && testo ? testo : riserva;
  } catch (_) {
    return riserva;
  }
};

const eAudioOVideo = (tipo) => tipo.startsWith('audio/') || tipo.startsWith('video/');

// Get extracted text with progress tracking.
// I `case` restano stringhe letterali: uploadFormats.test.js li legge dal
// sorgente per confrontarli con l'elenco dei formati accettati.
export const getExtractedText = async (typeFile, base64Data, onProgress = null, fileName = null) => {
  // Il mime puo' arrivare con i parametri attaccati (`audio/webm;codecs=opus`
  // da MediaRecorder). Lo `switch` confronta stringhe intere: senza ripulirlo
  // il file cadeva su `default`. Il tipo pulito e' anche quello che va al
  // server, che ricava l'estensione dal mime.
  const tipo = mimePulito(typeFile);

  switch (tipo) {
    // Audio e video: tutti finiscono a Whisper.
    // webm/ogg escono dal MediaRecorder del browser e dai messaggi vocali;
    // m4a e mov sono quello che registra un iPhone. Senza di loro il file
    // arrivava fin qui e usciva `null`, cioe' "Nessun testo estratto": il
    // caricamento riuscito e l'utente senza niente in mano. Il tipo va passato
    // PULITO (senza `;codecs=opus`), perche' il server ricava l'estensione dal
    // mime.
    case 'audio/mpeg':
    case 'video/wav':
    case 'audio/wav':
    case 'audio/mp3':
    case 'video/mp3':
    case 'audio/mp4':
    case 'video/mp4':
    case 'audio/webm':
    case 'video/webm':
    case 'audio/ogg':
    case 'video/ogg':
    case 'audio/x-m4a':
    case 'audio/m4a':
    case 'audio/aac':
    case 'video/quicktime': {
      const audioBlob = base64ToBlob(base64Data, tipo);
      const generatedText = await transcribeAudio(audioBlob);
      return generatedText.text;
    }
    case 'application/pdf': {
      return await extractTextFromPdf(base64Data, onProgress, fileName || 'PDF');
    }
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
      return await extractTextFromWord(base64Data);
    }
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
      return await extractTextFromPpt(base64Data);
    }
    // .doc e .ppt: i formati Office di prima del 2007. Sono contenitori OLE2,
    // nessuna libreria del browser li apre. Il server invece li legge gia':
    // riconosce l'OLE2 dai magic byte. Qui c'era solo da tirare il filo.
    // Il nome del file conta: e' l'estensione che dice al server se e' un
    // documento o una presentazione.
    case 'application/msword': {
      return await processDocxOnServer(base64Data, fileName || 'documento.doc');
    }
    case 'application/vnd.ms-powerpoint': {
      return await processDocxOnServer(base64Data, fileName || 'presentazione.ppt');
    }
    case 'text/plain': {
      return atob(base64Data);
    }
    default:
      return { message: messaggioFormatoNonSupportato(typeFile, fileName) };
  }
};

// ─────────────────────────────────────────────────────────────────────────
// IL LETTORE DI PDF: un worker per file, che resta aperto fra testo e immagini
// ─────────────────────────────────────────────────────────────────────────

/**
 * Il worker di `workers/fileExtractWorker.js` visto dal thread principale.
 * Una richiesta alla volta; le `progress` intermedie vanno a `onProgress`.
 */
const RISPOSTE_DEL_LETTORE = new Set(['done', 'immagini', 'chiuso', 'error']);

class LettorePdf {
  constructor(file, onProgress = null) {
    this.file = file;
    this.onProgress = onProgress;
    this.worker = new Worker(new URL('../../workers/fileExtractWorker.js', import.meta.url));
    this.inCorso = null;
    this.worker.onmessage = (msg) => {
      const dati = msg.data || {};
      if (dati.type === 'progress') {
        if (this.onProgress) {
          this.onProgress({ phase: dati.phase, progress: dati.progress, message: dati.message });
        }
        return;
      }
      // pdf.js, caricato con importScripts, annuncia `ready` a chi ascolta
      // sul worker: non e' una risposta nostra, e non deve chiudere niente.
      if (!this.inCorso || !RISPOSTE_DEL_LETTORE.has(dati.type)) return;
      const { atteso, risolvi, rifiuta } = this.inCorso;
      this.inCorso = null;
      if (dati.type === 'error') rifiuta(new Error(dati.message));
      else if (dati.type === atteso) risolvi(dati);
      else rifiuta(new Error(`Risposta inattesa dal worker: ${dati.type}`));
    };
    this.worker.onerror = (err) => {
      const errore = new Error(err?.message || 'Il worker di lettura si e\' fermato');
      if (this.inCorso) {
        const { rifiuta } = this.inCorso;
        this.inCorso = null;
        rifiuta(errore);
      }
    };
  }

  chiedi(messaggio, atteso) {
    if (this.inCorso) return Promise.reject(new Error('Il worker ha gia\' una richiesta in corso'));
    return new Promise((risolvi, rifiuta) => {
      this.inCorso = { atteso, risolvi, rifiuta };
      this.worker.postMessage(messaggio);
    });
  }

  /** @returns {Promise<{text:string, pagine:number, caratteri:number, pagineSenzaTesto:number}>} */
  testo() {
    return this.chiedi(
      { azione: 'testo', file: this.file, fileName: this.file.name, mimeType: 'application/pdf', soloLocale: true },
      'done'
    );
  }

  /** @returns {Promise<Array<{pagina:number, image_data:string, mime_type:string}>>} */
  async immagini(da, a) {
    const risposta = await this.chiedi({ azione: 'immagini', da, a }, 'immagini');
    return risposta.pagine;
  }

  chiudi() {
    this.worker.terminate();
  }
}

/** Vero dove le pagine si possono rendere in immagini fuori dal thread. */
const supportaOcrLocale = () =>
  typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined';

/**
 * L'OCR di una scansione senza far salire il PDF: le pagine escono dal
 * worker a lotti di JPEG e vanno a `processMultipleImages`, che tiene in
 * volo un lotto mentre il worker rende il successivo.
 */
const ocrScansioneLocale = async (lettore, pagine, onProgress, fileName) => {
  const throttled = onProgress ? createProgressThrottle(onProgress, 2000) : null;
  let parziale = null;

  const testo = await processMultipleImages(
    { totale: pagine, prendiLotto: (da, a) => lettore.immagini(da, a) },
    {
      onProgress: (p) => {
        if (throttled) {
          throttled({ phase: 'ocr_processing', progress: p.progress, message: p.message });
        }
      },
      onStatusUpdate: (stato) => console.log(`📊 OCR Status: ${stato.status} - ${stato.message}`),
      onParziale: (conti) => {
        parziale = conti;
      },
    }
  );

  if (parziale) {
    console.warn(
      `[documenti] OCR PARZIALE di "${fileName}": ${parziale.perse} pagine su ${parziale.totali} perse` +
      (parziale.pagine?.length ? ` (pagine ${parziale.pagine.join(', ')})` : '')
    );
  }

  return {
    text: testo,
    // Stessa forma dei conti del server: chi mostra l'avviso (useGetDocuments,
    // backgroundFileProcessor) legge chunks_failed e chunks_total.
    estrazioneParziale: parziale
      ? { parziale: true, chunks_failed: parziale.perse, chunks_total: parziale.totali, failed_pages: parziale.pagine }
      : null,
  };
};

/** Il file su Storage e il testo dal server; l'unica strada per cio' che non entra in una richiesta. */
const viaCondottoLargo = async (file, onProgress) => {
  let viaStorage;
  try {
    viaStorage = await estraiViaStorage(file, { onProgress });
  } catch (errore) {
    // Chi chiama (backgroundFileProcessor) riprova da solo il condotto se
    // processFile RIFIUTA con un file grande. Qui il condotto e' gia' stato
    // percorso: l'errore torna come esito, non come rifiuto, per non farlo
    // salire due volte. E' il contratto che questa strada ha sempre avuto.
    errore.condottoGiaPercorso = true;
    throw errore;
  }
  return {
    text: viaStorage.text,
    estrazioneParziale: viaStorage.parziale ? viaStorage.metadata : null,
  };
};

/**
 * Il PDF: pdf.js nel worker a qualsiasi taglia; se e' una scansione, OCR
 * delle pagine come immagini; solo se il browser non puo', il file sale.
 */
const estraiPdf = async (file, onProgress) => {
  let lettore = null;
  try {
    lettore = new LettorePdf(file, onProgress);
    const locale = await lettore.testo();
    pubblicaMetrichePdf(file, locale);

    if (!testoScarso(locale)) {
      return { text: locale.text, estrazioneParziale: null };
    }
    console.log(
      `Resa locale scarsa (${locale.caratteri} caratteri su ${locale.pagine} pagine, ` +
      `${locale.pagineSenzaTesto} senza testo): probabile scansione`
    );

    if (!supportaOcrLocale()) {
      console.log('OffscreenCanvas assente: le pagine non si possono rendere qui, il PDF sale');
    } else if (locale.pagine > MAX_PAGES_FOR_OCR_FALLBACK) {
      console.log(`${locale.pagine} pagine superano il limite di ${MAX_PAGES_FOR_OCR_FALLBACK} per l'OCR locale`);
    } else {
      try {
        return await ocrScansioneLocale(lettore, locale.pagine, onProgress, file.name);
      } catch (erroreOcr) {
        console.log('OCR locale non riuscito, il PDF sale:', erroreOcr.message);
      }
    }
  } catch (erroreLocale) {
    // Cifrato, malformato, o un worker che non parte: non e' un errore
    // dell'utente, e' il segnale che tocca al server.
    pubblicaMetrichePdf(file, { pagine: 0, caratteri: 0 });
    console.log('Lettura locale non riuscita, il PDF sale:', erroreLocale.message);
  } finally {
    if (lettore) lettore.chiudi();
  }

  if (passaDalCondottoLargo(file)) {
    return viaCondottoLargo(file, onProgress);
  }
  const text = await extractTextFromPdf(file, onProgress, file.name);
  return { text, estrazioneParziale: null };
};

const firmaZipValida = (arrayBuffer) => {
  const firma = new Uint8Array(arrayBuffer.slice(0, 4));
  return [0x50, 0x4B, 0x03, 0x04].every((byte, i) => byte === firma[i]);
};

/** Il DOCX: mammoth in locale a qualsiasi taglia, il server solo se rende poco o non apre. */
const estraiDocx = async (file, onProgress) => {
  try {
    const { default: mammoth } = await import('mammoth/mammoth.browser');
    const arrayBuffer = await leggiComeArrayBuffer(file);
    if (!firmaZipValida(arrayBuffer)) {
      throw new Error('File .docx non valido: formato non riconosciuto');
    }
    const { value } = await mammoth.extractRawText({ arrayBuffer });
    if (value && value.trim().length >= MIN_CARATTERI_DOCUMENTO) {
      return { text: value, estrazioneParziale: null };
    }
    console.log(`Mammoth ha reso ${(value || '').trim().length} caratteri: provo dal server`);
  } catch (erroreLocale) {
    console.warn('Lettura locale del .docx non riuscita, provo dal server:', erroreLocale.message);
  }

  if (passaDalCondottoLargo(file)) {
    return viaCondottoLargo(file, onProgress);
  }
  const text = await processDocxOnServer(await leggiComeBase64(file), file.name);
  return { text, estrazioneParziale: null };
};

/** Il PPTX: JSZip in locale a qualsiasi taglia, il server solo se rende poco o non apre. */
const estraiPptx = async (file, onProgress) => {
  try {
    const text = await testoDalleDiapositive(file);
    if (text && text.trim().length >= MIN_CARATTERI_DOCUMENTO) {
      return { text, estrazioneParziale: null };
    }
    console.log(`Le diapositive hanno reso ${(text || '').trim().length} caratteri: provo dal server`);
  } catch (erroreLocale) {
    console.warn('Lettura locale del .pptx non riuscita, provo dal server:', erroreLocale.message);
  }

  if (passaDalCondottoLargo(file)) {
    return viaCondottoLargo(file, onProgress);
  }
  const text = await processDocxOnServer(await leggiComeBase64(file), file.name);
  return { text, estrazioneParziale: null };
};

/** .doc e .ppt: nessuna libreria del browser li apre, li legge il server. */
const estraiOfficeLegacy = async (file, onProgress) => {
  if (passaDalCondottoLargo(file)) {
    return viaCondottoLargo(file, onProgress);
  }
  const text = await processDocxOnServer(await leggiComeBase64(file), file.name);
  return { text, estrazioneParziale: null };
};

/**
 * Audio e video: dal video parte solo l'audio, quando si puo' staccare qui.
 * Poi la strada di sempre: Storage sopra i 20 MiB, base64 sotto.
 */
const estraiAudioVideo = async (file, tipo, onProgress) => {
  let media = file;
  if (puoStaccareLAudio(file)) {
    try {
      media = await staccaTracciaAudio(file, { onProgress });
      console.log(
        `Traccia audio staccata: ${(file.size / 1048576).toFixed(1)} MB di video -> ` +
        `${(media.size / 1048576).toFixed(1)} MB di ${media.type}`
      );
    } catch (erroreAudio) {
      // Nessuna traccia copiabile, o ffmpeg che non parte: il video sale
      // intero come prima.
      console.warn('Audio non staccabile, il video sale intero:', erroreAudio.message);
      media = file;
    }
  }

  if (passaDalCondottoLargo(media)) {
    return viaCondottoLargo(media, onProgress);
  }
  const text = await getExtractedText(media.type || tipo, await leggiComeBase64(media), onProgress, media.name);
  return { text, estrazioneParziale: null };
};

/**
 * L'esito di un file che non si e' letto. Oltre al messaggio porta i fatti
 * che servono a dire allo studente PERCHE' (utils/erroreEstrazione.js): il
 * codice HTTP, il codice dell'errore (Storage, rete), il formato che non
 * sappiamo leggere. Solo quelli che si sanno: una chiave vuota non c'e'.
 */
const esitoErrore = (file, errorMessage, fatti = {}) => {
  const esito = {
    name: file.name,
    type: file.type,
    extractedText: '',
    errorMessage,
  };
  if (Number.isInteger(fatti.status)) esito.status = fatti.status;
  if (typeof fatti.codice === 'string' && fatti.codice) esito.codice = fatti.codice;
  if (fatti.formatoNonSupportato) esito.formatoNonSupportato = true;
  return esito;
};

/** I fatti di un errore lanciato: codice HTTP e codice dell'errore. */
const fattiDellErrore = (error) => ({
  status: error?.status ?? error?.response?.status,
  codice: error?.code,
});

/**
 * Da un file al suo testo. Risolve `{ name, type, extractedText }` (piu'
 * `estrazioneParziale` se dei pezzi sono andati persi, e `metadata` per le
 * note Obsidian); rifiuta con `{ name, type, extractedText: '', errorMessage }`.
 */
export const processFile = async (file, onProgress = null) => {
  // I file .md arrivano con `type` incoerente tra browser (spesso '' perché
  // .md non è un MIME riconosciuto nativamente, a volte 'text/markdown').
  // Vanno trattati come testo puro esattamente come .txt, riconoscendoli
  // dall'estensione invece che fidarsi del MIME.
  const isMarkdownFile = /\.(md|markdown)$/i.test(file.name || '');
  const isPlainTextFile = file.type === 'text/plain' || isMarkdownFile;

  if (isMarkdownFile) {
    const fileContent = await file.text();
    const { body, frontmatter, tags, wikilinks, embeds, links } = parseObsidianNote(fileContent);

    // Il minimo di 300 caratteri si misura sul body (frontmatter escluso): una
    // nota con tanto frontmatter e poco testo non deve passare, una con poco
    // frontmatter e testo legittimo non deve essere penalizzata.
    if (!body || body.length < MIN_CARATTERI_DOCUMENTO) {
      const errorMessage = `Inserire un documento maggiore di 300 caratteri. Lunghezza documento attuale ${body.length}.`;
      toast.error(errorMessage);
    }

    // metadata.obsidian solo se c'è qualcosa da riportare: una nota .md senza
    // frontmatter/tag/wikilink/embed/link non deve produrre una forma vuota.
    const hasObsidianMetadata = Boolean(frontmatter) || tags.length > 0 || wikilinks.length > 0 || embeds.length > 0 || links.length > 0;

    return {
      name: file.name,
      type: file.type,
      extractedText: body,
      ...(hasObsidianMetadata ? { metadata: { obsidian: { frontmatter, tags, wikilinks, embeds, links } } } : {}),
    };
  }

  if (isPlainTextFile) {
    const fileContent = await file.text();
    if (!fileContent || fileContent.length < MIN_CARATTERI_DOCUMENTO) {
      const errorMessage = `Inserire un documento maggiore di 300 caratteri. Lunghezza documento attuale ${fileContent.length}.`;
      toast.error(errorMessage);
    }
    return {
      name: file.name,
      type: file.type,
      extractedText: fileContent,
    };
  }

  const tipo = mimePulito(file.type);
  let esito;
  try {
    if (tipo.startsWith('image/')) {
      esito = { text: await extractTextFromImage(file), estrazioneParziale: null };
    } else if (tipo === 'application/pdf') {
      esito = await estraiPdf(file, onProgress);
    } else if (tipo === MIME_DOCX) {
      esito = await estraiDocx(file, onProgress);
    } else if (tipo === MIME_PPTX) {
      esito = await estraiPptx(file, onProgress);
    } else if (tipo === MIME_DOC || tipo === MIME_PPT) {
      esito = await estraiOfficeLegacy(file, onProgress);
    } else if (eAudioOVideo(tipo)) {
      esito = await estraiAudioVideo(file, tipo, onProgress);
    } else {
      // Il `.pages` e gli altri formati che non proviamo nemmeno a leggere:
      // il segno viaggia fino all'avviso, che dice di esportarlo in PDF.
      esito = {
        text: { message: messaggioFormatoNonSupportato(file.type, file.name), formatoNonSupportato: true },
        estrazioneParziale: null,
      };
    }
  } catch (error) {
    console.error('Error processing file:', error);
    // Il testo del server prima di quello del client: "Nessun parlato
    // riconosciuto" dice allo studente cosa fare, "Request failed with status
    // code 422" no. Prima qui restava solo il secondo (STU-424).
    const dettaglioServer = error?.response?.data?.detail;
    let errorMessage = typeof dettaglioServer === 'string' && dettaglioServer.trim()
      ? dettaglioServer
      : error?.message || 'Errore durante l\'elaborazione del file';
    if (tipo === MIME_DOCX && (errorMessage.includes('corrupt') || errorMessage.includes('zip'))) {
      errorMessage = 'File .docx corrotto o non valido. Prova a:\n' +
                    '• Riaprire e salvare il file in Word\n' +
                    '• Convertire il file in formato PDF\n' +
                    '• Verificare che il file non sia danneggiato';
    }
    const fatti = fattiDellErrore(error);
    if (error?.condottoGiaPercorso) return esitoErrore(file, errorMessage, fatti);
    throw esitoErrore(file, errorMessage, fatti);
  }

  const extractedText = esito.text;
  if (!extractedText || typeof extractedText === 'object') {
    throw esitoErrore(file, extractedText?.message ?? 'Nessun testo estratto', {
      formatoNonSupportato: Boolean(extractedText?.formatoNonSupportato),
    });
  }

  if (extractedText.length < MIN_CARATTERI_DOCUMENTO) {
    // Niente toast qui: l'avviso di un file che non si legge lo manda il
    // router action, uno per file e con il rimedio. Un secondo avviso per lo
    // stesso fallimento era rumore (STU-424).
    throw esitoErrore(
      file,
      `Inserire un documento maggiore di 300 caratteri. Lunghezza documento attuale ${extractedText.length}.`
    );
  }

  return {
    name: file.name,
    type: file.type,
    extractedText,
    ...(esito.estrazioneParziale ? { estrazioneParziale: esito.estrazioneParziale } : {}),
  };
};

export default processFile;
